import moment                                                              from 'moment';
import { FC, useCallback, useContext, useEffect, useLayoutEffect, useRef } from 'react';
import { useHistory, useLocation }                                         from 'react-router';
import { useAuth0 }                                                        from '@auth0/auth0-react';

import { CONSTANTS }                                                          from '../../../../constants';
import { SystemEvent }                                                        from '../../../../types';
import { useLastActivityTimestamp, useLogout, useWindowExit, useWindowTouch } from '../../../../hooks';
import { DynamicComponentsContext }                                           from '../../../contexts';
import { AUTH_ZERO_ERROR_FLAGS }                                              from '../external/AuthZeroProvider';


interface UserActivityMonitorProps {
    timeoutMin?: number,
    pingMS?: number,

    [ additionalProps: string ]: any
}

const defaultProps: UserActivityMonitorProps = {
    timeoutMin: CONSTANTS.SYSTEM.SESSION.ACTIVITY_SESSION_MIN,
    pingMS:     CONSTANTS.SYSTEM.POLLING.INTERVAL_MS
}

const UserActivityMonitor: FC<UserActivityMonitorProps> = ( props ) => {

    let history                           = useHistory(),
        location                          = useLocation(),
        { logout }                        = useLogout(),
        { modal: [ , modalActions ] }     = useContext( DynamicComponentsContext ),
        { sidebar: [ , sidebarActions ] } = useContext( DynamicComponentsContext ),
        { toasts: [ , toastActions ] }    = useContext( DynamicComponentsContext ),
        { removeWindowExitListeners }     = useWindowExit(),
        { getAccessTokenSilently }        = useAuth0();

    // Reference to the timer monitoring the activity usage
    let activityPoll = useRef( null );

    // Activity timestamp method
    let { getLastActivityTimestamp, setLastActivityTimestamp } = useLastActivityTimestamp();

    /* CALLBACK TO LOGOUT */
    // -----------------------------------------------------------------------
    let logUserOut = useCallback( async () => {

        clearInterval( activityPoll.current );

        // close any modals, sidebars, toasts
        modalActions.toggle( false );
        sidebarActions.toggle( false );
        toastActions.removeAll();

        // Clean up listeners
        removeWindowExitListeners();

        // Perform logout
        logout( () => {

            // Show a message about activity timeout
            toastActions.warning( 'Activity Timeout', 'You have been logged out due to inactivity', { timeoutMS: 0 } );

        } );

    }, [ history, location ] );

    /* CHECK ACCESS FROM AUTH0 AND ACTIVITY LOG */
    // -----------------------------------------------------------------------
    let checkAccess = useCallback( async () => {

        // check auth0's state
        try{

            await getAccessTokenSilently();

        } catch( e ) {

            if( e.error === AUTH_ZERO_ERROR_FLAGS.LOGIN_REQUIRED ){

                logUserOut();
            }
            if( e.error === AUTH_ZERO_ERROR_FLAGS.CONSENT_REQUIRED ){

                logUserOut();
            }

            throw e;
        }

        // compare against session timeout period MS
        let now             = moment(),
            lastActivity    = getLastActivityTimestamp(),
            currentTimediff = now.diff( lastActivity, 'minutes' ),
            currentTimeLeft = ( props.timeoutMin - currentTimediff );

        let noActivityOccurredWithinTimeframe = lastActivity && ( currentTimediff > props.timeoutMin ),
            activityOccurredWithinTimeframe   = lastActivity?.isBetween( now, now.subtract( props.timeoutMin, 'minutes' ), null, '()' ) ?? false;

        // No movement from user? Close "session"
        if( noActivityOccurredWithinTimeframe ){

            console.log( `[SESSION]: No activity detected for ${ props.timeoutMin } minutes, logging off.` );
            logUserOut();

        }

        // Activity logged, should extend the "session"
        else if( activityOccurredWithinTimeframe ){

            console.log( `[SESSION]: Extending use session.` );
            setLastActivityTimestamp();

        } else {

            // console.log( `[SESSION]: User has ${ currentTimeLeft } min(s) left...` );
        }

        return Promise.resolve();

    }, [ history, location ] )

    /* START HEARTBEAT MONITORING */
    // -----------------------------------------------------------------------
    useEffect( () => {

        checkAccess();

        // start polling
        activityPoll.current = setInterval( () => {

            try{

                checkAccess();

            } catch( e ) {

                logUserOut();
            }

        }, props.pingMS );

        // on unmount, remove polling
        return () => {

            clearInterval( activityPoll.current );
        }

    }, [] );

    /* CALLBACK TO HANDLE USER WINDOW CLICK / TOUCH */
    // -----------------------------------------------------------------------
    let handleUserTouchActivityDetected = ( e: SystemEvent ) => {

        setLastActivityTimestamp();
    };

    /* START TOUCH MONITORING */
    // -----------------------------------------------------------------------
    let { removeWindowTouchListeners } = useWindowTouch( handleUserTouchActivityDetected );

    useLayoutEffect( () => {

        // on unmount, remove touch listener
        return () => {

            removeWindowTouchListeners();
        }
    } )

    return null;
};

UserActivityMonitor.defaultProps = defaultProps;

export { UserActivityMonitor }
